import { type RefObject, useState, useEffect } from 'react';
import { useDragAndDropAsyncModules } from '../async-import/index.tsx';
import { cl } from '../constants.tsx';
import type { DraggableStatus } from '../types.tsx';
import { setStyle } from '../utils/set-style.tsx';

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const useReorderColumns = ({
	id,
	index,
	isDragDisabled,
	ref,
}: {
	id: string;
	index: number;
	isDragDisabled?: boolean;
	ref: RefObject<HTMLDivElement>;
}) => {
	const [status, setStatus] = useState<DraggableStatus>('idle');
	const { combine, draggable } = useDragAndDropAsyncModules();

	useEffect(() => {
		const el = ref.current;
		if (isDragDisabled || !el) {
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			return () => {};
		}

		let nextSiblingCleanupFn = noop;
		let tableBodyCleanupFn = noop;

		return combine(
			draggable({
				element: el,
				getInitialData() {
					return { type: 'table-header', id, index };
				},
				onGenerateDragPreview() {
					setStatus('preview');

					// make sure the drag preview doesn't have table scroll
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const tableBody = el
						.closest(`.${cl.BASE_TABLE}`)
						?.querySelector(`.${cl.BASE_TABLE_BODY}`) as HTMLElement;

					if (tableBody) {
						tableBodyCleanupFn = setStyle(tableBody, {
							overflow: 'hidden',
						});
					}

					// hide resize handle for the drag preview
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const nextSibling = el?.nextSibling as HTMLElement;
					if (nextSibling) {
						nextSiblingCleanupFn = setStyle(nextSibling, {
							visibility: 'hidden',
						});
					}
				},
				onDragStart() {
					setStatus('dragging');
					tableBodyCleanupFn();
				},
				onDrop() {
					setStatus('idle');
					nextSiblingCleanupFn();
				},
			}),
		);
	}, [isDragDisabled, id, index, ref, draggable, combine]);

	return { status };
};
